<script setup>
import { reactive, ref, computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { useI18n } from "vue-i18n";
import { required, email, minLength, maxLength, sameAs } from "@vuelidate/validators";
import router from "../router";
import { marked } from "marked";
import tosRaw from "../assets/tos.md?raw";
import PasswordInputComponent from "./PasswordInputComponent.vue";
import { useHttp } from "../composables/http";

const { t } = useI18n();
const $axios = useHttp();
const tosHtml = marked(tosRaw);

const user = reactive({
  name: "",
  email: "",
  password: "",
  password_confirmation: "",
  referral_code: "",
  app: import.meta.env.PUBLIC_APP || "timebrella",
});

const errors = ref([]);
const processing = ref(false);
const showTos = ref(false);

const rules = computed(() => ({
  user: {
    name: { required, minLength: minLength(3), maxLength: maxLength(256) },
    email: { required, email },
    password: { required, minLength: minLength(6) },
    password_confirmation: {
      required,
      minLength: minLength(6),
      sameAsPassword: sameAs(user.password),
    },
  },
}));

const v$ = useVuelidate(rules, { user });

async function registration() {
  const isFormCorrect = await v$.value.$validate();
  if (isFormCorrect) {
    processing.value = true;
    await $axios
      .post("/users", user)
      .then(() => {
        return router.push({ name: "registration.success" });
      })
      .catch((error) => {
        errors.value =
          error.response && error.response.data ? error.response.data.errors : "";
        processing.value = false;
      });
  }
}
</script>

<template>
  <div class="mp-form">
    <h5 class="text-center mb-3">{{ t("registration.title") }}</h5>
    <form action="#" autocomplete="off" method="post" @submit.prevent="registration">
      <div class="mb-3">
        <label for="name" class="form-label">{{ t("registration.name") }}</label>
        <input
          id="name"
          v-model="user.name"
          type="text"
          name="name"
          :placeholder="t('registration.namePlaceholder')"
          class="form-control"
          autocomplete="false"
        />
        <div v-if="v$.user.name.$errors.length > 0" class="mt-1 alert alert-danger">
          <span v-for="error of v$.user.name.$errors" :key="error.$uid">{{
            error.$message
          }}</span>
        </div>
      </div>

      <div class="mb-3">
        <label for="email" class="form-label">{{ t("registration.email") }}</label>
        <input
          id="email"
          v-model="user.email"
          type="text"
          name="email"
          :placeholder="t('registration.emailPlaceholder')"
          class="form-control"
          autocomplete="false"
        />
        <div v-if="v$.user.email.$errors.length > 0" class="mt-1 alert alert-danger">
          <span v-for="error of v$.user.email.$errors" :key="error.$uid">{{
            error.$message
          }}</span>
        </div>
        <div
          v-if="errors.email && errors.email.length > 0"
          class="mt-1 alert alert-danger"
        >
          <span v-for="(error, index) of errors.email" :key="index">{{ error }}</span>
        </div>
      </div>

      <div class="mb-3">
        <label for="password" class="form-label">{{ t("registration.password") }}</label>
        <PasswordInputComponent
          id="password"
          v-model="user.password"
          name="password"
          :placeholder="t('registration.passwordPlaceholder')"
          input-class="form-control"
          autocomplete="false"
        />
        <div v-if="v$.user.password.$errors.length > 0" class="mt-1 alert alert-danger">
          <span v-for="error of v$.user.password.$errors" :key="error.$uid">{{
            error.$message
          }}</span>
        </div>
        <div
          v-if="errors.password && errors.password.length > 0"
          class="mt-1 alert alert-danger"
        >
          <span v-for="(error, index) of errors.password" :key="index">{{ error }}</span>
        </div>
      </div>

      <div class="mb-3">
        <label for="password_confirmation" class="form-label">{{
          t("registration.confirmPassword")
        }}</label>
        <PasswordInputComponent
          id="password_confirmation"
          v-model="user.password_confirmation"
          name="password_confirmation"
          :placeholder="t('registration.confirmPasswordPlaceholder')"
          input-class="form-control"
          autocomplete="false"
        />
        <div
          v-if="v$.user.password_confirmation.$errors.length > 0"
          class="mt-1 alert alert-danger"
        >
          <span
            v-for="error of v$.user.password_confirmation.$errors"
            :key="error.$uid"
            >{{ error.$message }}</span
          >
        </div>
      </div>

      <div class="mb-3">
        <label for="referral_code" class="form-label">{{
          t("registration.referralCode")
        }}</label>
        <input
          id="referral_code"
          v-model="user.referral_code"
          type="text"
          name="referral_code"
          :placeholder="t('registration.referralCodePlaceholder')"
          class="form-control"
        />
        <div
          v-if="errors.referral_code && errors.referral_code.length > 0"
          class="mt-1 alert alert-danger"
        >
          <span v-for="(error, index) of errors.referral_code" :key="index">{{
            error
          }}</span>
        </div>
      </div>

      <div class="mb-3 text-center">
        <a href="#" class="mp-link" @click.prevent="showTos = true">{{
          t("registration.agreement")
        }}</a>
      </div>

      <div class="mb-3 text-center">
        <button type="submit" :disabled="processing" class="btn mp-btn-primary w-100">
          {{
            processing ? t("registration.plsWait") : t("registration.confirmAndSignUp")
          }}
        </button>
      </div>

      <div class="text-center mt-3">
        <label>
          {{ t("registration.alreadyHaveAnAccount") }}
          <router-link :to="{ name: 'login' }" class="mp-link">{{
            t("registration.loginNow")
          }}</router-link>
        </label>
      </div>
    </form>

    <teleport to="body">
      <div v-if="showTos" class="tos-overlay" @click.self="showTos = false">
        <div class="tos-modal">
          <button class="tos-close" @click="showTos = false">&times;</button>
          <div class="tos-content" v-html="tosHtml"></div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.tos-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.tos-modal {
  background: #fff;
  border-radius: 8px;
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
}

.tos-close {
  position: sticky;
  top: 0;
  float: right;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 0.25rem;
  color: #666;
}

.tos-close:hover {
  color: #000;
}

.tos-content :deep(h1) {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.tos-content :deep(h2) {
  font-size: 1.2rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.tos-content :deep(h3) {
  font-size: 1.05rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.tos-content :deep(p) {
  margin-bottom: 0.75rem;
  line-height: 1.6;
}

.tos-content :deep(ul) {
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}

.tos-content :deep(li) {
  margin-bottom: 0.25rem;
}

.tos-content :deep(hr) {
  margin: 1.5rem 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

.tos-content :deep(a) {
  color: #0d6efd;
  text-decoration: underline;
}

.tos-content :deep(strong) {
  font-weight: 600;
}
</style>
